import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, Cell } from "recharts";
import { Loader2, Activity, CheckCircle2, XCircle, Clock, Gauge, Mail, ClipboardList, Palette, Save, LogOut, Plug, DollarSign, Calculator, ShieldCheck, RotateCcw, Database, Search, Star, MapPin, Landmark, Sparkles, RefreshCw, Play, Plus, ExternalLink, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, fmtMoney, formatApiError } from "@/lib/api";
import { scoreColor } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { PricingCatalogPanel, ReviewQueuePanel } from "./PricingAdmin";
import { GoLiveChecklistPanel } from "./GoLiveChecklist";

const CAT_LABELS = {
  website: "Website", conversion: "Customer Conversion", presence: "Online Presence",
  reputation: "Reputation", competitive: "Competitive",
};

export default function Admin() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [branding, setBranding] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [savingBrand, setSavingBrand] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/admin/stats"), api.get("/branding"), api.get("/admin/integrations")])
      .then(([s, b, i]) => { setStats(s.data); setBranding(b.data); setIntegrations(i.data.integrations); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const setB = (k, v) => setBranding((x) => ({ ...x, [k]: v }));
  const saveBranding = async () => {
    setSavingBrand(true);
    try { const { data } = await api.put("/branding", branding); setBranding(data); toast.success("Branding saved — new PDFs will use it."); }
    catch { toast.error("Could not save branding."); }
    finally { setSavingBrand(false); }
  };

  if (loading) return <div className="min-h-[70vh] grid place-items-center"><Loader2 className="w-7 h-7 text-indigo-600 animate-spin" /></div>;
  if (!stats) return <div className="min-h-[70vh] grid place-items-center text-slate-500">Could not load admin stats.</div>;

  const t = stats.totals || {};
  const fp = stats.fix_plan || {};
  const catData = (stats.opportunity_categories || []).map((c) => ({ name: CAT_LABELS[c.category] || c.category, count: c.count }));

  return (
    <div className="bg-slate-50 min-h-screen">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-indigo-600 font-semibold text-xs uppercase tracking-[0.2em] mb-2">Internal</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Admin Dashboard</h1>
            <p className="mt-2 text-slate-600">Usage insights to learn which problems and opportunities matter most.</p>
          </div>
          <div className="flex items-center gap-3">
            {user?.email && <span className="hidden sm:inline text-sm text-slate-500" data-testid="admin-current-user">{user.email}</span>}
            <Button onClick={logout} data-testid="admin-logout"
              className="rounded-lg bg-white border border-slate-200 hover:border-slate-300 text-slate-700 font-medium px-4">
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>

        {/* Launch readiness — what's left before accepting real payments (honest, key-ready) */}
        <GoLiveChecklistPanel />

        {/* Data Providers — real multi-provider status + live health checks */}
        <DataProvidersPanel />

        {/* Integrations & data source status (Phase 6) */}
        <IntegrationsPanel integrations={integrations} />

        {/* Financial Impact Engine settings (single source of truth for pricing + modeling) */}
        <FinancialSettingsPanel />

        {/* Provisional pricing review queue (must clear before a proposal can be paid) */}
        <ReviewQueuePanel />

        {/* Service pricing rules + catalog (authoritative source for proposal pricing) */}
        <PricingCatalogPanel />

        {/* Licenses & Compliance settings (evidence-first) */}
        <ComplianceSettingsPanel />

        {/* Industry Source Registry — admin-managed review platforms, directories & registries */}
        <SourceRegistryPanel />

        {/* Agency operator role management (platform admin only) */}
        <RoleManagementPanel />

        {/* Consultation / booking requests inbox + operator notification email */}
        <ConsultationInboxPanel />

        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-10">
          <Kpi icon={Activity} label="Total scans" value={t.scans} />
          <Kpi icon={CheckCircle2} label="Completed" value={t.complete} color="text-emerald-600" />
          <Kpi icon={Clock} label="In progress" value={t.in_progress} color="text-indigo-600" />
          <Kpi icon={XCircle} label="Failed" value={t.failed} color="text-red-600" />
          <Kpi icon={Gauge} label="Avg health" value={t.avg_health_score ?? "–"} colorHex={t.avg_health_score != null ? scoreColor(t.avg_health_score) : undefined} />
          <Kpi icon={Mail} label="Leads" value={t.leads} color="text-indigo-600" />
        </div>

        {/* Fix Plan overview */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm mb-10" data-testid="admin-fixplan">
          <div className="flex items-center gap-2 mb-4">
            <ClipboardList className="w-5 h-5 text-slate-700" />
            <h3 className="font-heading font-semibold text-slate-900">Fix Plan activity</h3>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <MiniStat label="Opportunities tracked" value={fp.tracked ?? 0} />
            <MiniStat label="Completed" value={fp.completed ?? 0} color="#059669" />
            <MiniStat label="Overdue" value={fp.overdue ?? 0} color="#ea580c" />
            <MiniStat label="Est. opportunity (owner)" value={`$${Number(fp.estimated_opportunity || 0).toLocaleString()}`} sub="estimated" />
            <MiniStat label="Recovered (confirmed)" value={`$${Number(fp.money_recovered || 0).toLocaleString()}`} sub="owner-confirmed" color="#059669" />
          </div>
        </div>

        {/* Refund requests */}
        <RefundRequestsPanel />

        {/* Branding / white-label */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 sm:p-8 shadow-sm mb-10" data-testid="admin-branding">
          <div className="flex items-center gap-2 mb-1">
            <Palette className="w-5 h-5 text-indigo-600" />
            <h3 className="font-heading text-xl font-semibold text-slate-900">Report branding (white-label)</h3>
          </div>
          <p className="text-sm text-slate-500 mb-6">Brand the downloadable PDF report with your company details. Applies to all newly generated PDFs.</p>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Company name</Label>
              <Input data-testid="brand-company" value={branding?.company_name || ""} onChange={(e) => setB("company_name", e.target.value)} placeholder="Your company" />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Logo URL</Label>
              <Input data-testid="brand-logo" value={branding?.logo_url || ""} onChange={(e) => setB("logo_url", e.target.value)} placeholder="https://…/logo.png" />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Contact email</Label>
              <Input data-testid="brand-email" value={branding?.contact_email || ""} onChange={(e) => setB("contact_email", e.target.value)} placeholder="team@company.com" />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Contact phone</Label>
              <Input data-testid="brand-phone" value={branding?.contact_phone || ""} onChange={(e) => setB("contact_phone", e.target.value)} placeholder="(808) 555-0000" />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Website</Label>
              <Input data-testid="brand-website" value={branding?.website || ""} onChange={(e) => setB("website", e.target.value)} placeholder="company.com" />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Brand color</Label>
              <div className="flex items-center gap-3">
                <input type="color" data-testid="brand-color" value={branding?.brand_color || "#4f46e5"} onChange={(e) => setB("brand_color", e.target.value)}
                  className="w-11 h-10 rounded-lg border border-slate-200 cursor-pointer bg-white" />
                <Input data-testid="brand-color-hex" value={branding?.brand_color || "#4f46e5"} onChange={(e) => setB("brand_color", e.target.value)} className="font-mono" />
              </div>
            </div>
            <div className="md:col-span-2">
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Report footer note <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Textarea data-testid="brand-footer" rows={2} value={branding?.footer_note || ""} onChange={(e) => setB("footer_note", e.target.value)} placeholder="e.g. Prepared exclusively for our clients by …" />
            </div>
          </div>
          <div className="mt-6">
            <Button onClick={saveBranding} disabled={savingBrand} data-testid="brand-save"
              className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold px-6 active:scale-95 transition-transform">
              {savingBrand ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save branding
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-10">
          {/* Opportunity categories chart */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm min-w-0">
            <h3 className="font-heading font-semibold text-slate-900 mb-1">Most frequent opportunity categories</h3>
            <p className="text-xs text-slate-500 mb-4">Across all completed scans.</p>
            {catData.length ? (
              <div className="w-full min-w-0 overflow-hidden">
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={catData} layout="vertical" margin={{ left: 10, right: 20 }}>
                    <XAxis type="number" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 12, fill: "#334155" }} axisLine={false} tickLine={false} />
                    <RTooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                    <Bar dataKey="count" radius={[0, 6, 6, 0]} fill="#4f46e5" barSize={22} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : <Empty />}
          </div>

          {/* Top problems */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm min-w-0">
            <h3 className="font-heading font-semibold text-slate-900 mb-1">Most common problems detected</h3>
            <p className="text-xs text-slate-500 mb-4">The issues showing up most often.</p>
            <div className="space-y-2.5">
              {(stats.top_problems || []).length ? stats.top_problems.map((p, i) => (
                <div key={i} className="flex items-center justify-between gap-3">
                  <span className="text-sm text-slate-700 truncate">{p.problem}</span>
                  <span className="shrink-0 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold px-2.5 py-0.5">{p.count}</span>
                </div>
              )) : <Empty />}
            </div>
          </div>
        </div>

        {/* Business types */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm mb-10">
          <h3 className="font-heading font-semibold text-slate-900 mb-4">Businesses scanned by type</h3>
          <div className="flex flex-wrap gap-2">
            {(stats.business_types || []).length ? stats.business_types.map((b, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm text-slate-700">
                {b.type} <b className="text-slate-900">{b.count}</b>
              </span>
            )) : <Empty />}
          </div>
        </div>

        {/* Leads */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-10" data-testid="admin-leads">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-heading font-semibold text-slate-900">Sales leads</h3>
            <span className="text-xs text-slate-500">{(stats.recent_leads || []).length} recent</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-slate-500">
                <tr>
                  <th className="px-6 py-3 font-medium">Name</th>
                  <th className="px-6 py-3 font-medium">Business</th>
                  <th className="px-6 py-3 font-medium">Email</th>
                  <th className="px-6 py-3 font-medium">Phone</th>
                  <th className="px-6 py-3 font-medium">Interest</th>
                  <th className="px-6 py-3 font-medium">Emailed</th>
                  <th className="px-6 py-3 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {(stats.recent_leads || []).map((l, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-6 py-3 font-medium text-slate-800">{l.name}</td>
                    <td className="px-6 py-3 text-slate-600">{l.business_name}</td>
                    <td className="px-6 py-3 text-slate-600">{l.email}</td>
                    <td className="px-6 py-3 text-slate-600">{l.phone || "—"}</td>
                    <td className="px-6 py-3 text-slate-600">{l.interest}</td>
                    <td className="px-6 py-3">
                      <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${l.email_notified ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-slate-500 bg-slate-50 border-slate-200"}`}>
                        {l.email_notified ? "sent" : "saved"}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-slate-500">{l.created_at ? new Date(l.created_at).toLocaleString() : "—"}</td>
                  </tr>
                ))}
                {!(stats.recent_leads || []).length && <tr><td colSpan={7} className="px-6 py-10 text-center text-slate-400">No leads yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent scans table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100"><h3 className="font-heading font-semibold text-slate-900">Recent scans</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-slate-500">
                <tr>
                  <th className="px-6 py-3 font-medium">Business</th>
                  <th className="px-6 py-3 font-medium">Category</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                  <th className="px-6 py-3 font-medium text-right">Score</th>
                  <th className="px-6 py-3 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {(stats.recent_scans || []).map((s, i) => (
                  <tr key={i} onClick={() => s.status === "complete" && nav(`/report/${s.id}`)}
                    className={`border-t border-slate-100 ${s.status === "complete" ? "hover:bg-slate-50 cursor-pointer" : ""}`}>
                    <td className="px-6 py-3 font-medium text-slate-800">{s.name}</td>
                    <td className="px-6 py-3 text-slate-600">{s.category}</td>
                    <td className="px-6 py-3"><StatusBadge status={s.status} /></td>
                    <td className="px-6 py-3 text-right font-semibold" style={{ color: s.health_score != null ? scoreColor(s.health_score) : "#94a3b8" }}>{s.health_score ?? "–"}</td>
                    <td className="px-6 py-3 text-slate-500">{s.created_at ? new Date(s.created_at).toLocaleString() : "–"}</td>
                  </tr>
                ))}
                {!(stats.recent_scans || []).length && <tr><td colSpan={5} className="px-6 py-10 text-center text-slate-400">No scans yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, color = "text-slate-900", colorHex }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
      <Icon className={`w-5 h-5 mb-3 ${color}`} />
      <div className="font-heading text-2xl font-bold leading-none" style={colorHex ? { color: colorHex } : undefined}>{value ?? 0}</div>
      <div className="text-xs text-slate-500 mt-1.5">{label}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    complete: "text-emerald-700 bg-emerald-50 border-emerald-200",
    failed: "text-red-700 bg-red-50 border-red-200",
    running: "text-indigo-700 bg-indigo-50 border-indigo-200",
    pending: "text-slate-600 bg-slate-50 border-slate-200",
  };
  return <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${map[status] || map.pending}`}>{status}</span>;
}

function Empty() {
  return <p className="text-sm text-slate-400">No data yet.</p>;
}

function MiniStat({ label, value, sub, color = "#0f172a" }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50/60 p-4">
      <div className="font-heading text-xl font-bold leading-none" style={{ color }}>{value}</div>
      <div className="text-[11px] text-slate-500 mt-1.5 leading-tight">{label}</div>
      {sub && <div className="text-[10px] text-slate-400 mt-0.5 uppercase tracking-wide">{sub}</div>}
    </div>
  );
}

const INTEG_STATUS = {
  CONFIGURED: { cls: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "Configured" },
  NOT_CONFIGURED: { cls: "text-amber-700 bg-amber-50 border-amber-200", label: "Not configured" },
  ERROR: { cls: "text-red-700 bg-red-50 border-red-200", label: "Error" },
};

const REFUND_BADGE = {
  REQUESTED: "bg-sky-100 text-sky-700", PROCESSING: "bg-indigo-100 text-indigo-700",
  REFUNDED: "bg-red-100 text-red-700", PARTIALLY_REFUNDED: "bg-amber-100 text-amber-700",
  DENIED: "bg-slate-200 text-slate-600", FAILED: "bg-red-100 text-red-700",
};

function ConsultationInboxPanel() {
  const [list, setList] = useState(null);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [providerOk, setProviderOk] = useState(false);
  const [savedEmail, setSavedEmail] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/consultations").then(({ data }) => {
    setList(data.consultations || []);
    setNotifyEmail(data.notify_email || "");
    setSavedEmail(data.notify_email || "");
    setProviderOk(!!data.provider_configured);
  }).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const saveEmail = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/consultation-settings", { notify_email: notifyEmail });
      setSavedEmail(data.notify_email || "");
      toast.success(data.notify_email ? `Notifications will go to ${data.notify_email}` : "Notification email cleared");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const handle = async (id) => {
    setBusyId(id);
    try { await api.post(`/admin/consultations/${id}/handle`); toast.success("Marked handled"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const NOTIFY = {
    sent: "bg-emerald-100 text-emerald-700", not_configured: "bg-slate-100 text-slate-500",
    failed: "bg-red-100 text-red-700", pending: "bg-amber-100 text-amber-700",
  };
  const pending = (list || []).filter((c) => c.status === "NEW");

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm mb-10" data-testid="admin-consultations">
      <h2 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2"><Mail className="w-5 h-5 text-indigo-600" /> Consultation requests {pending.length > 0 && <span className="rounded-full bg-indigo-600 text-white text-xs px-2 py-0.5">{pending.length} new</span>}</h2>

      <div className="mt-3 mb-5 rounded-lg border border-slate-200 bg-slate-50 p-4">
        <Label className="text-xs font-semibold text-slate-600">Operator notification email</Label>
        <div className="flex flex-col sm:flex-row gap-2 mt-1.5">
          <Input type="email" placeholder="you@agency.com" value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)} data-testid="consult-notify-email" className="max-w-sm" />
          <Button onClick={saveEmail} disabled={saving || notifyEmail === savedEmail} data-testid="consult-notify-save" className="bg-slate-900 hover:bg-slate-800">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
          </Button>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          {providerOk
            ? (savedEmail ? `New requests email ${savedEmail}.` : "Email provider is ready — set an address to receive notifications. Until then, requests are still saved below.")
            : "Email provider not configured — requests are saved below but no email is sent."}
        </p>
      </div>

      {list === null ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : list.length === 0 ? (
        <p className="text-sm text-slate-400">No consultation requests yet.</p>
      ) : (
        <div className="space-y-2" data-testid="consult-list">
          {list.map((c) => (
            <div key={c.public_id} className="rounded-lg border border-slate-200 p-4" data-testid={`consult-row-${c.public_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-slate-800">{c.name} <span className="text-slate-400 font-normal">· {c.email}</span>{c.phone ? <span className="text-slate-400 font-normal"> · {c.phone}</span> : ""}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{c.service_name || "General enquiry"}{c.business_name ? ` · ${c.business_name}` : ""}</p>
                  {c.preferred_time && <p className="text-xs text-slate-500">Best time: {c.preferred_time}</p>}
                  {c.message && <p className="text-sm text-slate-600 mt-1.5">{c.message}</p>}
                </div>
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${c.status === "HANDLED" ? "bg-slate-200 text-slate-600" : "bg-indigo-100 text-indigo-700"}`}>{c.status}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${NOTIFY[c.notify_status] || NOTIFY.pending}`}>email: {c.notify_status}</span>
                  {c.status === "NEW" && <Button size="sm" variant="outline" disabled={busyId === c.public_id} onClick={() => handle(c.public_id)} data-testid={`consult-handle-${c.public_id}`}>Mark handled</Button>}
                </div>
              </div>
              <p className="text-[10px] text-slate-400 mt-2">{new Date(c.created_at).toLocaleString()}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function RoleManagementPanel() {
  const [users, setUsers] = useState(null);
  const [audit, setAudit] = useState([]);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [showAudit, setShowAudit] = useState(false);

  const load = (q = "") =>
    api.get(`/admin/users${q ? `?search=${encodeURIComponent(q)}` : ""}`)
      .then(({ data }) => setUsers(data.users || [])).catch(() => setUsers([]));
  const loadAudit = () => api.get("/admin/role-audit").then(({ data }) => setAudit(data.changes || [])).catch(() => {});
  useEffect(() => { load(); loadAudit(); }, []);

  const setRole = async (u, role) => {
    setBusyId(u.id);
    try {
      await api.post(`/admin/users/${u.id}/role`, { role });
      toast.success(role === "agency" ? `${u.email} is now an agency operator` : `${u.email} set to customer`);
      load(search); loadAudit();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const ROLE_BADGE = {
    super_admin: "bg-slate-900 text-white", admin: "bg-slate-900 text-white",
    agency: "bg-indigo-100 text-indigo-700", customer: "bg-slate-100 text-slate-600",
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm mb-10" data-testid="admin-roles">
      <div className="flex items-center justify-between gap-3 mb-1">
        <h2 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-indigo-600" /> Agency operators</h2>
        <button onClick={() => setShowAudit((v) => !v)} data-testid="roles-audit-toggle" className="text-sm text-indigo-600 hover:underline">{showAudit ? "Hide audit log" : "View audit log"}</button>
      </div>
      <p className="text-sm text-slate-500 mb-4">Grant the <b>agency operator</b> role to let a user see internal pricing, margins and the internal sales sheet. Platform admins can't be changed here.</p>

      <div className="flex gap-2 mb-4 max-w-md">
        <Input placeholder="Search by name or email" value={search} onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load(search)} data-testid="roles-search" />
        <Button variant="outline" onClick={() => load(search)} data-testid="roles-search-btn"><Search className="w-4 h-4" /></Button>
      </div>

      {users === null ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : users.length === 0 ? (
        <p className="text-sm text-slate-400">No users found.</p>
      ) : (
        <div className="space-y-2" data-testid="roles-user-list">
          {users.map((u) => {
            const isAdmin = ["admin", "super_admin"].includes(u.role);
            return (
              <div key={u.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3" data-testid={`user-row-${u.id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-slate-800 truncate">{u.name || u.email}</p>
                  <p className="text-xs text-slate-500 truncate">{u.email}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase ${ROLE_BADGE[u.role] || ROLE_BADGE.customer}`} data-testid={`user-role-${u.id}`}>{u.role}</span>
                  {isAdmin ? (
                    <span className="text-xs text-slate-400">platform admin</span>
                  ) : u.role === "agency" ? (
                    <Button size="sm" variant="outline" disabled={busyId === u.id} onClick={() => setRole(u, "customer")} data-testid={`revoke-agency-${u.id}`} className="text-red-600 border-red-200">Revoke</Button>
                  ) : (
                    <Button size="sm" disabled={busyId === u.id} onClick={() => setRole(u, "agency")} data-testid={`grant-agency-${u.id}`} className="bg-indigo-600 hover:bg-indigo-700">Make operator</Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showAudit && (
        <div className="mt-5 border-t border-slate-100 pt-4" data-testid="roles-audit-log">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">Role change history</h3>
          {audit.length === 0 ? <p className="text-sm text-slate-400">No role changes yet.</p> : (
            <div className="space-y-1.5 max-h-60 overflow-auto">
              {audit.map((c, i) => (
                <div key={i} className="text-xs text-slate-500 flex flex-wrap gap-x-2" data-testid={`audit-row-${i}`}>
                  <span className="text-slate-700 font-medium">{c.target_email}</span>
                  <span>{c.old_role} → <b className="text-slate-700">{c.new_role}</b></span>
                  <span>by {c.changed_by_email}</span>
                  <span className="text-slate-400">{new Date(c.at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function RefundRequestsPanel() {
  const [list, setList] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () => api.get("/admin/refunds").then(({ data }) => setList(data.refunds || [])).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const act = async (id, action, amount) => {
    setBusyId(id);
    try {
      const body = {};
      if (action === "deny") body.admin_note = window.prompt("Reason for denial (optional):") || "";
      if (action === "approve" && amount != null) body.amount = amount;
      await api.post(`/admin/refunds/${id}/${action}`, body);
      toast.success(action === "approve" ? "Refund processed via Stripe" : "Request denied");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const pending = (list || []).filter((r) => r.status === "REQUESTED");

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm mb-10" data-testid="admin-refunds">
      <div className="flex items-center gap-2 mb-4">
        <RotateCcw className="w-5 h-5 text-slate-700" />
        <h3 className="font-heading font-semibold text-slate-900">Refund requests</h3>
        {pending.length > 0 && <span className="rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold px-2 py-0.5">{pending.length} pending</span>}
      </div>
      {!list ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : list.length === 0 ? (
        <p className="text-sm text-slate-400">No refund requests.</p>
      ) : (
        <div className="space-y-2">
          {list.map((r) => {
            const cur = (r.currency || "usd").toUpperCase();
            return (
              <div key={r.public_id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-4" data-testid={`admin-refund-${r.public_id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 truncate">{fmtMoney(r.amount_requested, cur)} · {r.business_name || r.proposal_id || "Order"}</p>
                  <p className="text-xs text-slate-500 truncate">{r.reason} · requested {(r.created_at || "").slice(0, 10)}</p>
                  {r.error && <p className="text-xs text-red-600">{r.error}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${REFUND_BADGE[r.status] || "bg-slate-100 text-slate-600"}`}>{r.status.replace("_", " ")}</span>
                  {(r.status === "REQUESTED") && (
                    <>
                      <Button size="sm" disabled={busyId === r.public_id} onClick={() => act(r.public_id, "approve")} data-testid={`refund-approve-${r.public_id}`} className="bg-emerald-600 hover:bg-emerald-700">
                        {busyId === r.public_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Approve"}
                      </Button>
                      <Button size="sm" variant="outline" disabled={busyId === r.public_id} onClick={() => act(r.public_id, "deny")} data-testid={`refund-deny-${r.public_id}`} className="text-red-600 border-red-200">Deny</Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


const PROV_ICON = { brave_search: Search, google_places: MapPin, yelp: Star, openstreetmap: MapPin,
  tripadvisor: Star, dcca: Landmark, ai_research: Sparkles };

function provDisplay(p) {
  // Honest, explicit status — never collapse "not configured" into a green check.
  if (p.configured && p.last_result === "success") return { label: "CONNECTED", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", ok: true };
  if (p.configured && p.last_result === "error") return { label: "ERROR", cls: "bg-red-50 text-red-700 border-red-200", ok: false };
  if (p.configured) return { label: p.testable ? "CONFIGURED" : "AVAILABLE", cls: "bg-indigo-50 text-indigo-700 border-indigo-200", ok: true };
  if (!p.testable && (p.key === "tripadvisor" || p.key === "dcca")) return { label: "SEARCH DISCOVERY", cls: "bg-sky-50 text-sky-700 border-sky-200", ok: false };
  return { label: "NOT CONFIGURED", cls: "bg-amber-50 text-amber-700 border-amber-200", ok: false };
}

function DataProvidersPanel() {
  const [providers, setProviders] = useState(null);
  const [summary, setSummary] = useState(null);
  const [testing, setTesting] = useState(null); // provider key or "all"

  const load = () => api.get("/admin/providers").then(({ data }) => { setProviders(data.providers); setSummary(data.summary); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const testOne = async (key) => {
    setTesting(key);
    try {
      const { data } = await api.post("/admin/providers/test", { provider: key });
      toast[data.ok ? "success" : "error"](`${data.name}: ${data.ok ? "working" : "not working"}${data.message ? " — " + data.message : ""}`);
      await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setTesting(null);
  };

  const testAll = async () => {
    setTesting("all");
    try {
      const { data } = await api.post("/admin/providers/test-all");
      toast.success(`Tested ${data.summary.total} providers · ${data.summary.ok} responding`);
      await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setTesting(null);
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 mb-8 shadow-sm" data-testid="data-providers-panel">
      <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
        <div className="flex items-center gap-2">
          <Database className="w-5 h-5 text-indigo-600" />
          <h2 className="font-heading text-lg font-semibold text-slate-900">Data Providers</h2>
          {summary && <span className="text-xs text-slate-400">{summary.configured}/{summary.total} configured</span>}
        </div>
        <Button size="sm" onClick={testAll} disabled={testing === "all"} data-testid="test-all-providers"
          className="bg-slate-900 hover:bg-slate-800">
          {testing === "all" ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Testing…</> : <><Play className="w-4 h-4 mr-1.5" /> Test all providers</>}
        </Button>
      </div>
      <p className="text-sm text-slate-500 mb-5">Real multi-provider business research. API keys are stored server-side (Secrets manager) and never shown here. Add a key to activate a provider — the rest keep working regardless.</p>
      {!providers ? <div className="py-6 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-indigo-600" /></div> : (
        <div className="grid sm:grid-cols-2 gap-3">
          {providers.map((p) => {
            const st = provDisplay(p);
            const Icon = PROV_ICON[p.key] || Database;
            return (
              <div key={p.key} data-testid={`provider-${p.key}`} className="rounded-lg border border-slate-200 p-4">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon className="w-4 h-4 text-slate-400 shrink-0" />
                    <p className="text-sm font-semibold text-slate-800 truncate">{p.name}</p>
                  </div>
                  <span data-testid={`provider-${p.key}-status`} className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide shrink-0 ${st.cls}`}>
                    {st.ok && <CheckCircle2 className="w-3 h-3 inline mr-0.5 -mt-0.5" />}{st.label}
                  </span>
                </div>
                <p className="text-[11px] text-slate-500">{p.type}</p>
                <p className="text-[12px] text-slate-600 mt-1">{p.capability}</p>
                {!p.configured && p.required_env?.length > 0 && (
                  <p className="text-[11px] text-slate-400 mt-1.5">Set: <span className="font-mono">{p.required_env.join(", ")}</span></p>
                )}
                {p.last_message && (
                  <p className={`text-[11px] mt-1.5 ${p.last_result === "error" ? "text-red-500" : "text-slate-400"}`}>
                    Last test: {p.last_result || "—"}{p.last_message ? ` · ${p.last_message}` : ""}
                  </p>
                )}
                {p.testable && (
                  <button onClick={() => testOne(p.key)} disabled={testing === p.key} data-testid={`provider-${p.key}-test`}
                    className="mt-2 inline-flex items-center gap-1 text-[12px] font-medium text-indigo-600 hover:underline disabled:opacity-50">
                    {testing === p.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Test
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function IntegrationsPanel({ integrations }) {
  if (!integrations) return null;
  // Preferred display order; any additional connectors from the API still render (never drop silently).
  const ORDER = ["google_places", "reviews", "competitors", "search", "news", "directory",
                 "public_records", "resend"];
  const keys = [...ORDER.filter((k) => integrations[k]),
                ...Object.keys(integrations).filter((k) => !ORDER.includes(k))];
  const rows = keys.map((k) => [k, integrations[k]]).filter(([, v]) => v);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 mb-8 shadow-sm" data-testid="integrations-panel">
      <div className="flex items-center gap-2 mb-1">
        <Plug className="w-5 h-5 text-indigo-600" />
        <h2 className="font-heading text-lg font-semibold text-slate-900">Integrations & Data Sources</h2>
      </div>
      <p className="text-sm text-slate-500 mb-5">External services powering verified data and notifications. API keys are stored server-side and never shown here.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {rows.map(([key, v]) => {
          const st = INTEG_STATUS[v.status] || INTEG_STATUS.NOT_CONFIGURED;
          return (
            <div key={key} data-testid={`integration-${key}`} className="rounded-lg border border-slate-200 p-4">
              <div className="flex items-center justify-between gap-2 mb-1">
                <p className="text-sm font-semibold text-slate-800">{v.label}</p>
                <span data-testid={`integration-${key}-status`} className={`rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${st.cls}`}>{st.label}</span>
              </div>
              <p className="text-xs text-slate-500">{v.provider}{v.powers ? ` · ${v.powers.join(", ")}` : ""}</p>
              {v.status !== "CONFIGURED" && v.required_env && (
                <p className="text-[11px] text-slate-400 mt-1.5">Set: <span className="font-mono">{v.required_env.join(", ")}</span></p>
              )}
              {v.last_delivery && (v.last_delivery.status || v.last_delivery.at) && (
                <p className="text-[11px] text-slate-400 mt-1.5">
                  Last delivery: {v.last_delivery.status || "—"}{v.last_delivery.at ? ` · ${new Date(v.last_delivery.at).toLocaleString()}` : ""}
                  {v.last_delivery.error ? ` · ${v.last_delivery.error}` : ""}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


const CURRENCIES = [
  { code: "USD", symbol: "$" }, { code: "EUR", symbol: "€" }, { code: "GBP", symbol: "£" },
  { code: "CAD", symbol: "$" }, { code: "AUD", symbol: "$" }, { code: "INR", symbol: "₹" },
];
const MODE_LABELS = { conservative: "Conservative", balanced: "Balanced", opportunity: "Opportunity" };

function FinancialSettingsPanel() {
  const [s, setS] = useState(null);
  const [modes, setModes] = useState(["conservative", "balanced", "opportunity"]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/financial-settings")
      .then(({ data }) => { setS(data.settings); if (data.modes?.length) setModes(data.modes); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!s) return null;

  const pricing = s.pricing || {};
  const pct = Math.round((pricing.service_fraction ?? 0.1) * 1000) / 10; // e.g. 10 (%)
  const minPrice = pricing.min_price ?? 1500;
  const sym = s.currency_symbol || "$";

  const setPricing = (patch) => setS((x) => ({ ...x, pricing: { ...x.pricing, ...patch } }));
  const setPct = (v) => setPricing({ service_fraction: Math.max(0, Math.min(100, Number(v) || 0)) / 100 });
  const setMin = (v) => setPricing({ min_price: Math.max(0, Number(v) || 0) });
  const setCurrency = (code) => {
    const c = CURRENCIES.find((x) => x.code === code) || CURRENCIES[0];
    setS((x) => ({ ...x, currency: c.code, currency_symbol: c.symbol }));
  };

  // Live example (adjusted annual opportunity = 100,000)
  const example = 100000;
  const calc = example * (pricing.service_fraction ?? 0.1);
  const suggested = Math.max(calc, minPrice);

  const save = async () => {
    if (pct < 0 || pct > 100) { toast.error("Pricing percentage must be between 0 and 100."); return; }
    if (minPrice < 0) { toast.error("Minimum price cannot be negative."); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/admin/financial-settings", {
        enabled: s.enabled, mode: s.mode, currency: s.currency, currency_symbol: s.currency_symbol,
        industry_tailoring: s.industry_tailoring,
        pricing: { method: "percentage", service_fraction: pricing.service_fraction, min_price: minPrice },
      });
      setS(data.settings);
      toast.success("Financial settings saved — new scans will use these.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save financial settings.");
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 sm:p-8 mb-8 shadow-sm" data-testid="financial-settings-panel">
      <div className="flex items-center gap-2 mb-1">
        <DollarSign className="w-5 h-5 text-indigo-600" />
        <h2 className="font-heading text-xl font-semibold text-slate-900">Financial Impact Settings</h2>
      </div>
      <p className="text-sm text-slate-500 mb-6">
        The single source of truth for financial modeling and service pricing. New scans use these settings;
        completed scans keep the configuration they were computed with.
      </p>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Engine on/off */}
        <div className="flex items-center justify-between rounded-lg border border-slate-200 p-4 md:col-span-2">
          <div>
            <p className="text-sm font-semibold text-slate-800">Financial engine</p>
            <p className="text-xs text-slate-500">When off, scans still run but no financial opportunity is calculated.</p>
          </div>
          <div className="flex items-center gap-2">
            <span data-testid="fin-set-status" className={`text-xs font-semibold uppercase tracking-wide ${s.enabled ? "text-emerald-600" : "text-slate-400"}`}>{s.enabled ? "On" : "Off"}</span>
            <Switch data-testid="fin-set-enabled" checked={!!s.enabled} onCheckedChange={(v) => setS((x) => ({ ...x, enabled: v }))} />
          </div>
        </div>

        {/* Industry tailoring on/off */}
        <div className="flex items-center justify-between rounded-lg border border-slate-200 p-4 md:col-span-2">
          <div>
            <p className="text-sm font-semibold text-slate-800">Industry tailoring</p>
            <p className="text-xs text-slate-500">Automatically apply trade-specific models (restaurant, salon, contractor…). Off = standard model. Never inflates totals.</p>
          </div>
          <div className="flex items-center gap-2">
            <span data-testid="fin-set-industry-status" className={`text-xs font-semibold uppercase tracking-wide ${s.industry_tailoring ? "text-indigo-600" : "text-slate-400"}`}>{s.industry_tailoring ? "On" : "Off"}</span>
            <Switch data-testid="fin-set-industry" checked={s.industry_tailoring !== false} onCheckedChange={(v) => setS((x) => ({ ...x, industry_tailoring: v }))} />
          </div>
        </div>

        {/* Modeling mode */}
        <div>
          <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Modeling mode</Label>
          <Select value={s.mode || "conservative"} onValueChange={(v) => setS((x) => ({ ...x, mode: v }))}>
            <SelectTrigger data-testid="fin-set-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              {modes.map((m) => <SelectItem key={m} value={m} data-testid={`fin-set-mode-${m}`}>{MODE_LABELS[m] || m}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-slate-400 mt-1.5">Conservative uses lower uplift factors and caps totals; higher modes widen estimates.</p>
        </div>

        {/* Currency */}
        <div>
          <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Currency</Label>
          <Select value={s.currency || "USD"} onValueChange={setCurrency}>
            <SelectTrigger data-testid="fin-set-currency"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code} data-testid={`fin-set-currency-${c.code}`}>{c.code} ({c.symbol})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Pricing % */}
        <div>
          <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Service pricing (% of adjusted annual opportunity)</Label>
          <div className="relative">
            <Input data-testid="fin-set-pct" type="number" min="0" max="100" step="0.5" value={pct} onChange={(e) => setPct(e.target.value)} className="pr-8" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
          </div>
        </div>

        {/* Min price */}
        <div>
          <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Minimum service investment</Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{sym}</span>
            <Input data-testid="fin-set-min" type="number" min="0" step="50" value={minPrice} onChange={(e) => setMin(e.target.value)} className="pl-7" />
          </div>
        </div>
      </div>

      {/* Live example calculation */}
      <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50/70 p-4" data-testid="fin-set-example">
        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2"><Calculator className="w-4 h-4" /> Example calculation</div>
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1 text-sm">
          <Row label="Estimated Annual Opportunity" value={`${sym}${example.toLocaleString()}`} />
          <Row label="Pricing Percentage" value={`${pct}%`} />
          <Row label="Calculated Service Investment" value={`${sym}${Math.round(calc).toLocaleString()}`} />
          <Row label="Minimum Service Investment" value={`${sym}${minPrice.toLocaleString()}`} />
          <div className="sm:col-span-2 border-t border-slate-200 mt-1 pt-1">
            <Row label="Suggested Service Investment" value={`${sym}${Math.round(suggested).toLocaleString()}`} bold />
          </div>
        </div>
        <p className="text-[11px] text-slate-400 mt-3">
          Formula: max(Adjusted Annual Opportunity × Pricing %, Minimum). Financial figures are modeled estimates
          based on available evidence and assumptions — not guaranteed financial results.
        </p>
      </div>

      <div className="mt-6">
        <Button onClick={save} disabled={saving} data-testid="fin-set-save"
          className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold px-6 active:scale-95 transition-transform">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save settings
        </Button>
      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className={`text-slate-600 ${bold ? "font-semibold text-slate-800" : ""}`}>{label}</span>
      <span className={`font-heading ${bold ? "text-indigo-700 font-bold" : "text-slate-800 font-semibold"}`}>{value}</span>
    </div>
  );
}

function ComplianceSettingsPanel() {
  const [s, setS] = useState(null);
  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [juris, setJuris] = useState("");

  useEffect(() => {
    api.get("/admin/compliance-settings")
      .then(({ data }) => {
        setS(data.settings); setVerification(data.verification || null);
        setJuris((data.settings.jurisdictions || []).join(", "));
      })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading || !s) return null;

  const rt = s.reminder_thresholds || { renewal_soon_days: 60, urgent_days: 14 };
  const setRt = (patch) => setS((x) => ({ ...x, reminder_thresholds: { ...x.reminder_thresholds, ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/compliance-settings", {
        enabled: s.enabled,
        exposure_modeling: s.exposure_modeling !== false,
        jurisdictions: juris.split(",").map((x) => x.trim()).filter(Boolean),
        reminder_thresholds: {
          renewal_soon_days: Math.max(0, Number(rt.renewal_soon_days) || 60),
          urgent_days: Math.max(0, Number(rt.urgent_days) || 14),
        },
      });
      setS(data.settings);
      toast.success("Compliance settings saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save compliance settings.");
    } finally { setSaving(false); }
  };

  const vStatus = verification?.configured ? "CONFIGURED" : "NOT_CONFIGURED";
  const vCls = verification?.configured ? INTEG_STATUS.CONFIGURED : INTEG_STATUS.NOT_CONFIGURED;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 sm:p-8 mb-8 shadow-sm" data-testid="compliance-settings-panel">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="w-5 h-5 text-indigo-600" />
        <h2 className="font-heading text-xl font-semibold text-slate-900">License &amp; Compliance</h2>
      </div>
      <p className="text-sm text-slate-500 mb-6">
        Jurisdiction-aware (Hawaii seeded; extensible to any US state/county/city). Evidence-first — statuses
        are never fabricated; without a verification source they show “Unable to verify” for confirmation with the issuing authority.
      </p>

      {/* Live verification source status */}
      <div className="rounded-lg border border-slate-200 p-4 mb-5 flex items-center justify-between gap-3" data-testid="compliance-verification-status">
        <div>
          <p className="text-sm font-semibold text-slate-800">Authoritative verification source</p>
          <p className="text-xs text-slate-500">Provider-agnostic &amp; key-ready. Set <span className="font-mono">LICENSE_VERIFY_PROVIDER</span> + <span className="font-mono">LICENSE_VERIFY_API_KEY</span> to verify live statuses, expirations &amp; fees against a government/official source.</p>
        </div>
        <span className={`shrink-0 rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${vCls.cls}`}>{vCls.label}</span>
      </div>

      <div className="space-y-5">
        <div className="flex items-center justify-between rounded-lg border border-slate-200 p-4">
          <div>
            <p className="text-sm font-semibold text-slate-800">Compliance scanning</p>
            <p className="text-xs text-slate-500">When off, scans skip the License &amp; Compliance section entirely.</p>
          </div>
          <div className="flex items-center gap-2">
            <span data-testid="comp-set-status" className={`text-xs font-semibold uppercase tracking-wide ${s.enabled ? "text-emerald-600" : "text-slate-400"}`}>{s.enabled ? "On" : "Off"}</span>
            <Switch data-testid="comp-set-enabled" checked={!!s.enabled} onCheckedChange={(v) => setS((x) => ({ ...x, enabled: v }))} />
          </div>
        </div>

        <div className="flex items-center justify-between rounded-lg border border-slate-200 p-4">
          <div>
            <p className="text-sm font-semibold text-slate-800">Modeled exposure</p>
            <p className="text-xs text-slate-500">Show a conservative MODELED financial exposure range for confirmed problem/expiring licenses. Clearly labeled — not an actual loss.</p>
          </div>
          <div className="flex items-center gap-2">
            <span data-testid="comp-set-exposure-status" className={`text-xs font-semibold uppercase tracking-wide ${s.exposure_modeling !== false ? "text-indigo-600" : "text-slate-400"}`}>{s.exposure_modeling !== false ? "On" : "Off"}</span>
            <Switch data-testid="comp-set-exposure" checked={s.exposure_modeling !== false} onCheckedChange={(v) => setS((x) => ({ ...x, exposure_modeling: v }))} />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-medium text-slate-700 mb-1.5 block">“Renewal soon” threshold (days before expiration)</Label>
            <Input data-testid="comp-set-renewal-days" type="number" min="0" value={rt.renewal_soon_days} onChange={(e) => setRt({ renewal_soon_days: e.target.value })} />
          </div>
          <div>
            <Label className="text-sm font-medium text-slate-700 mb-1.5 block">“Urgent” threshold (days before expiration)</Label>
            <Input data-testid="comp-set-urgent-days" type="number" min="0" value={rt.urgent_days} onChange={(e) => setRt({ urgent_days: e.target.value })} />
          </div>
        </div>

        <div>
          <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Supported jurisdictions (optional, comma-separated)</Label>
          <Input data-testid="comp-set-jurisdictions" value={juris} onChange={(e) => setJuris(e.target.value)} placeholder="e.g. Hawaii, California, Texas" />
          <p className="text-[11px] text-slate-400 mt-1.5">Hawaii is seeded with detailed authorities &amp; source references (labeled UNVERIFIED). The architecture supports adding more states/counties/cities without redesign.</p>
        </div>
      </div>
      <div className="mt-6">
        <Button onClick={save} disabled={saving} data-testid="comp-set-save"
          className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold px-6 active:scale-95 transition-transform">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save settings
        </Button>
      </div>
    </div>
  );
}



const INTEG_BADGE = {
  LIVE: { label: "Live API", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  NOT_CONFIGURED: { label: "API not connected", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  MANUAL: { label: "Manual / no API", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

const SRC_TYPE_LABEL = {
  review_platform: "Review platform", maps_directory: "Maps / directory", directory: "Directory",
  social_review: "Social", general_reputation: "Reputation", product_review: "Product reviews",
  government_registry: "Government registry", publication: "Publication",
};

const EMPTY_SRC = { id: "", platform: "", website: "", setup_url: "", industries: "general", source_type: "directory" };

function SourceRow({ s, onToggle, onInspect, onEdit, inspecting, inspection }) {
  const [open, setOpen] = useState(false);
  const b = INTEG_BADGE[s.integration_status] || INTEG_BADGE.MANUAL;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`source-row-${s.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-900">{s.platform}</span>
            <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${b.cls}`} data-testid={`source-integ-${s.id}`}>{b.label}</span>
            <span className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{SRC_TYPE_LABEL[s.source_type] || s.source_type}</span>
          </div>
          {s.website ? (
            <a href={s.website} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:underline inline-flex items-center gap-1 mt-0.5">{s.website.replace(/^https?:\/\//, "")} <ExternalLink className="w-3 h-3" /></a>
          ) : <span className="text-xs text-slate-400">No public website (registry / government source)</span>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-xs font-semibold ${s.enabled ? "text-emerald-600" : "text-slate-400"}`}>{s.enabled ? "On" : "Off"}</span>
          <Switch checked={!!s.enabled} onCheckedChange={(v) => onToggle(s.id, v)} data-testid={`source-toggle-${s.id}`} />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => onInspect(s.id)} disabled={inspecting} data-testid={`source-inspect-${s.id}`} className="text-indigo-600 border-indigo-200 hover:bg-indigo-50">
          {inspecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Search className="w-3.5 h-3.5 mr-1.5" /> Inspect</>}
        </Button>
        <Button size="sm" variant="outline" onClick={() => onEdit(s)} data-testid={`source-edit-${s.id}`} className="text-slate-600 border-slate-200 hover:bg-slate-50">
          <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
        </Button>
        <button type="button" onClick={() => setOpen((v) => !v)} data-testid={`source-detail-toggle-${s.id}`} className="text-xs font-medium text-slate-500 hover:text-slate-800">
          {open ? "Hide details" : "Access requirements & limits"}
        </button>
      </div>

      {inspection && (
        <div className={`mt-3 rounded-lg border p-3 text-xs ${inspection.can_retrieve ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`} data-testid={`source-inspection-${s.id}`}>
          <p className="font-semibold">{inspection.can_retrieve ? "Data can be retrieved (live check passed)" : "Data cannot be retrieved right now"}</p>
          <p className="mt-0.5">{inspection.retrieval_detail?.message || inspection.retrieval_detail?.hint || (inspection.can_retrieve ? "Live API responded OK." : "No live retrieval.")}</p>
          <p className="mt-0.5 text-[11px] opacity-80">Integration: {inspection.integration_status} · checked {new Date(inspection.checked_at).toLocaleTimeString()}</p>
        </div>
      )}

      {open && (
        <div className="mt-3 grid sm:grid-cols-2 gap-2 text-xs text-slate-600 border-t border-slate-100 pt-3" data-testid={`source-detail-${s.id}`}>
          <p><span className="font-semibold text-slate-800">Access requirements:</span> {s.auth_requirements || "—"}</p>
          <p><span className="font-semibold text-slate-800">Data access:</span> {s.data_access || "—"}</p>
          <p className="sm:col-span-2"><span className="font-semibold text-slate-800">Known limitations:</span> {s.known_limitations || "—"}</p>
          {(s.available_metrics || []).length > 0 && <p className="sm:col-span-2"><span className="font-semibold text-slate-800">Available metrics:</span> {s.available_metrics.join(", ")}</p>}
          {s.setup_url && <a href={s.setup_url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline inline-flex items-center gap-1">Official claim/setup link <ExternalLink className="w-3 h-3" /></a>}
        </div>
      )}
    </div>
  );
}

function SourceRegistryPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [industry, setIndustry] = useState("all");
  const [inspectingId, setInspectingId] = useState(null);
  const [inspections, setInspections] = useState({});
  const [editing, setEditing] = useState(null); // source being edited (or EMPTY_SRC for add)
  const [savingEdit, setSavingEdit] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/source-registry")
      .then(({ data }) => setData(data))
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  if (loading) return null;
  if (!data) return null;

  const over = data.overrides || { disabled: [], overrides: {}, added: [] };
  const industries = data.industries || {};
  const sources = (data.sources || []).filter((s) => industry === "all" || (s.industries || []).includes(industry));

  const persist = async (field, value) => {
    try {
      const { data: res } = await api.put("/admin/source-registry", { [field]: value });
      await load();
      return res;
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); throw e; }
  };

  const toggle = async (id, on) => {
    const disabled = new Set(over.disabled || []);
    if (on) disabled.delete(id); else disabled.add(id);
    try { await persist("disabled", [...disabled]); toast.success(on ? "Source enabled." : "Source disabled."); } catch {}
  };

  const inspect = async (id) => {
    setInspectingId(id);
    try {
      const { data: res } = await api.post(`/admin/source-registry/${id}/inspect`);
      setInspections((x) => ({ ...x, [id]: res }));
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setInspectingId(null); }
  };

  const openEdit = (s) => setEditing({
    id: s.id, platform: s.platform || "", website: s.website || "", setup_url: s.setup_url || "",
    known_limitations: s.known_limitations || "", _existing: true,
  });

  const saveEdit = async () => {
    if (!editing) return;
    setSavingEdit(true);
    try {
      if (editing._existing) {
        const newOverrides = { ...(over.overrides || {}) };
        newOverrides[editing.id] = {
          platform: editing.platform, website: editing.website,
          setup_url: editing.setup_url, known_limitations: editing.known_limitations,
        };
        await persist("overrides", newOverrides);
        toast.success("Source updated.");
      } else {
        if (!editing.id.trim() || !editing.platform.trim()) { toast.error("A new source needs at least an id and a platform name."); setSavingEdit(false); return; }
        const added = [...(over.added || []), {
          id: editing.id.trim().toLowerCase().replace(/[^a-z0-9_]+/g, "_"),
          platform: editing.platform.trim(), website: editing.website.trim(),
          setup_url: editing.setup_url.trim(), source_type: editing.source_type || "directory",
          industries: [editing.industries || "general"],
        }];
        await persist("added", added);
        toast.success("Custom source added.");
      }
      setEditing(null);
    } catch { } finally { setSavingEdit(false); }
  };

  const removeCustom = async (id) => {
    const added = (over.added || []).filter((s) => s.id !== id);
    try { await persist("added", added); toast.success("Custom source removed."); setEditing(null); } catch {}
  };

  const liveCount = (data.sources || []).filter((s) => s.integration_status === "LIVE").length;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 sm:p-8 mb-8 shadow-sm" data-testid="source-registry-panel">
      <div className="flex items-center gap-2 mb-1">
        <Database className="w-5 h-5 text-indigo-600" />
        <h2 className="font-heading text-xl font-semibold text-slate-900">Industry Source Registry</h2>
      </div>
      <p className="text-sm text-slate-500 mb-5">
        The review platforms, directories and registries we map to each industry. Enable/disable sources, edit their
        details, add your own, and inspect whether a source&apos;s data can actually be retrieved. {liveCount} of {(data.sources || []).length} have a live API connected — the rest are discoverable websites verified manually.
      </p>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-slate-500">Industry</Label>
          <Select value={industry} onValueChange={setIndustry}>
            <SelectTrigger className="w-56 h-9" data-testid="source-industry-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All industries</SelectItem>
              {Object.entries(industries).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={load} data-testid="source-refresh" className="text-slate-600 border-slate-200"><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh</Button>
          <Button size="sm" onClick={() => setEditing({ ...EMPTY_SRC, _existing: false })} data-testid="source-add-btn" className="bg-slate-900 hover:bg-slate-800"><Plus className="w-3.5 h-3.5 mr-1.5" /> Add source</Button>
        </div>
      </div>

      <div className="space-y-3" data-testid="source-list">
        {sources.map((s) => (
          <SourceRow key={s.id} s={s} onToggle={toggle} onInspect={inspect} onEdit={openEdit}
            inspecting={inspectingId === s.id} inspection={inspections[s.id]} />
        ))}
        {sources.length === 0 && <p className="text-sm text-slate-500">No sources for this industry filter.</p>}
      </div>

      {/* Edit / Add dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="source-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editing?._existing ? `Edit ${editing?.platform || "source"}` : "Add a custom source"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3 mt-1">
              {!editing._existing && (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs mb-1 block">Source id *</Label><Input value={editing.id} onChange={(e) => setEditing((x) => ({ ...x, id: e.target.value }))} placeholder="e.g. nextdoor" data-testid="source-field-id" /></div>
                  <div><Label className="text-xs mb-1 block">Source type</Label>
                    <Select value={editing.source_type} onValueChange={(v) => setEditing((x) => ({ ...x, source_type: v }))}>
                      <SelectTrigger className="h-9" data-testid="source-field-type"><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(SRC_TYPE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              <div><Label className="text-xs mb-1 block">Platform name *</Label><Input value={editing.platform} onChange={(e) => setEditing((x) => ({ ...x, platform: e.target.value }))} data-testid="source-field-platform" /></div>
              <div><Label className="text-xs mb-1 block">Website</Label><Input value={editing.website} onChange={(e) => setEditing((x) => ({ ...x, website: e.target.value }))} placeholder="https://…" data-testid="source-field-website" /></div>
              <div><Label className="text-xs mb-1 block">Official claim / setup link</Label><Input value={editing.setup_url} onChange={(e) => setEditing((x) => ({ ...x, setup_url: e.target.value }))} placeholder="https://…" data-testid="source-field-setup" /></div>
              {!editing._existing && (
                <div><Label className="text-xs mb-1 block">Industry</Label>
                  <Select value={editing.industries} onValueChange={(v) => setEditing((x) => ({ ...x, industries: v }))}>
                    <SelectTrigger className="h-9" data-testid="source-field-industry"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(industries).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {editing._existing && (
                <div><Label className="text-xs mb-1 block">Known limitations (note)</Label><Textarea rows={2} value={editing.known_limitations} onChange={(e) => setEditing((x) => ({ ...x, known_limitations: e.target.value }))} data-testid="source-field-limitations" /></div>
              )}
              <div className="flex items-center justify-between pt-1">
                {editing._existing && (over.added || []).some((a) => a.id === editing.id)
                  ? <Button variant="outline" onClick={() => removeCustom(editing.id)} data-testid="source-remove-btn" className="text-red-600 border-red-200 hover:bg-red-50"><Trash2 className="w-4 h-4 mr-1.5" /> Remove</Button>
                  : <span />}
                <Button onClick={saveEdit} disabled={savingEdit} data-testid="source-save-btn" className="bg-slate-900 hover:bg-slate-800">
                  {savingEdit ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
